import * as AbsintheSocket from '@absinthe/socket';
import { createAbsintheSocketLink as createApolloAbsintheSocketLink } from '@absinthe/socket-apollo-link';
import type { FetchResult, Operation } from '@apollo/client';
import { ApolloLink, Observable } from '@apollo/client';
import { auth } from '@frameio/utils/auth';
import { isServer } from '@frameio/utils/config/helpers';
import { getCookie } from '@frameio/utils/cookies';
import { connectSocket } from './Socket';

// This error is used to signal a new BE connection
export const SocketResetError = new Error(
  'Socket Reset - likely from BE redeploy'
);
SocketResetError.name = 'NetworkError';

class AbsintheSocketLink extends ApolloLink {
  private currentToken: string | undefined;

  private socket: any | undefined; // Phoenix.Socket

  private absintheSocket: any | undefined; // AbsintheSocket

  private link!: ApolloLink;

  private activeObservers: Set<{ error: (error: Error) => void }> = new Set();

  constructor() {
    super();
    this.currentToken = auth.getAccessToken();

    const previewEnvironmentCookie = !isServer
      ? getCookie('x-preview-environment')
      : undefined;

    /**
     * Only initiate a connection if we have an access token and no preview environment
     * in the cookie. We do not have a way to initiate a connection with a preview environment
     * manually
     */
    if (this.currentToken && !previewEnvironmentCookie) {
      this.connectSocket();
    } else {
      // Create a "dummy" link that silently fails for unauthenticated users
      this.link = new ApolloLink(() => {
        return new Observable((observer) => {
          observer.complete();
        });
      });
    }
  }

  private connectSocket(): void {
    this.socket = connectSocket({
      onReset: () => this.emitError(SocketResetError),
    });
    this.absintheSocket = AbsintheSocket.create(this.socket);
    this.link = createApolloAbsintheSocketLink(this.absintheSocket);
  }

  private emitError(error: Error): void {
    this.activeObservers.forEach((observer) => {
      observer.error(error);
    });
    this.activeObservers.clear();
  }

  private checkTokenAndReconnectIfNeeded(): void {
    const newToken = auth.getAccessToken();

    // If we didn't have a token before but now we do, initialize the socket
    if (!this.currentToken && newToken) {
      this.currentToken = newToken;
      this.connectSocket();
      return;
    }

    // If token changed, reconnect
    if (newToken && newToken !== this.currentToken) {
      this.currentToken = newToken;
      // Triggers phoenix socket reconnect
      this.socket.conn.close();
    }

    // If we had a token but now we don't, clean up and set dummy link
    if (this.currentToken && !newToken) {
      this.currentToken = undefined;
      this.socket = undefined;
      this.absintheSocket = undefined;
      this.link = new ApolloLink(() => {
        return new Observable((observer) => {
          observer.complete();
        });
      });
    }
  }

  request(operation: Operation): Observable<FetchResult> {
    try {
      this.checkTokenAndReconnectIfNeeded();
      const result = this.link.request(operation);

      if (!result) {
        throw new Error('Socket link request returned null or undefined');
      }

      return new Observable((observer) => {
        this.activeObservers.add(observer);

        const subscription = result.subscribe({
          next: (value) => observer.next(value),
          error: (error) => {
            this.activeObservers.delete(observer);
            observer.error(error);
          },
          complete: () => {
            this.activeObservers.delete(observer);
            observer.complete();
          },
        });

        return () => {
          this.activeObservers.delete(observer);
          subscription.unsubscribe();
        };
      });
    } catch (error) {
      return new Observable((observer) => {
        observer.error(error);
      });
    }
  }
}

const createAbsintheSocketLink = () => {
  return new AbsintheSocketLink();
};

export default createAbsintheSocketLink;
